import { Routes } from '@angular/router';
import { permisoGuard } from '../core/auth/permiso.guard';
import { Permiso } from '../core/auth/permisos';

/** Sección a la que pertenece la pantalla (encabezado de la bandeja). */
const SECCION = 'Parasitología (PARA)';

/**
 * Sección de Parasitología. De sus siete servicios del RT-52 tienen formato
 * cargado la investigación de Nosema (IHIMV-RT-21 PARA 7), el Gusano Barrenador
 * del Ganado (PARA 15), la identificación de escarabajo / Aethina tumida
 * (PARA 6), los ectoparásitos (PARA 13) y los endoparásitos (PARA); los demás
 * siguen entrando por la bandeja genérica hasta que se defina el suyo.
 *
 * Ectoparásitos y endoparásitos tienen una sola bandeja para todas las
 * especies: el panel de parásitos lo decide el backend con la especie.
 *
 * La evaluación de ixodicida no vive acá aunque su formato se rotule
 * «Parasitología»: su sección receptora es Ixodología y tiene módulo propio.
 */
export const parasitologiaRoutes: Routes = [
  {
    path: 'parasitologia/nosema',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Investigación de Nosema',
    data: {
      pantalla: 'NOSEMA',
      breadcrumb: SECCION,
      titulo: 'Investigación de Nosema',
      permiso: Permiso.NOSEMA_VER,
    },
  },
  {
    path: 'parasitologia/nosema/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'NOSEMA',
      permiso: [Permiso.NOSEMA_RESULTADOS, Permiso.NOSEMA_VER],
    },
    loadComponent: () =>
      import('./pages/nosema-resultados/nosema-resultados.page').then(
        (m) => m.NosemaResultadosPage,
      ),
    title: 'SiLabVet | Informe de resultado — Investigación de Nosema',
  },
  {
    path: 'parasitologia/gusano-barrenador',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Gusano Barrenador del Ganado',
    data: {
      pantalla: 'GBG',
      breadcrumb: SECCION,
      titulo: 'Gusano Barrenador del Ganado',
      permiso: Permiso.GBG_VER,
    },
  },
  {
    path: 'parasitologia/gusano-barrenador/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'GBG',
      permiso: [Permiso.GBG_RESULTADOS, Permiso.GBG_VER],
    },
    loadComponent: () =>
      import('./pages/gbg-resultados/gbg-resultados.page').then(
        (m) => m.GbgResultadosPage,
      ),
    title: 'SiLabVet | Informe de resultado — Gusano Barrenador del Ganado',
  },
  {
    path: 'parasitologia/aethina-tumida',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Identificación de escarabajo',
    data: {
      pantalla: 'AETHINA_TUMIDA',
      breadcrumb: SECCION,
      titulo: 'Identificación de escarabajo (Aethina tumida)',
      permiso: Permiso.AETHINA_TUMIDA_VER,
    },
  },
  {
    path: 'parasitologia/aethina-tumida/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'AETHINA_TUMIDA',
      permiso: [Permiso.AETHINA_TUMIDA_RESULTADOS, Permiso.AETHINA_TUMIDA_VER],
    },
    loadComponent: () =>
      import('./pages/aethina-tumida-resultados/aethina-tumida-resultados.page').then(
        (m) => m.AethinaTumidaResultadosPage,
      ),
    title: 'SiLabVet | Informe de resultado — Identificación de escarabajo',
  },
  // Ectoparásitos: una sola bandeja y una sola hoja para todas las especies;
  // el panel de taxones lo decide el backend con la especie de la recepción.
  {
    path: 'parasitologia/ectoparasitos',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Ectoparásitos',
    data: {
      pantalla: 'ECTOPARASITOS',
      breadcrumb: SECCION,
      titulo: 'Ectoparásitos',
      permiso: Permiso.ECTOPARASITOS_VER,
    },
  },
  {
    path: 'parasitologia/ectoparasitos/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'ECTOPARASITOS',
      permiso: [Permiso.ECTOPARASITOS_RESULTADOS, Permiso.ECTOPARASITOS_VER],
    },
    loadComponent: () =>
      import('./pages/ectoparasitos-resultados/ectoparasitos-resultados.page').then(
        (m) => m.EctoparasitosResultadosPage,
      ),
    title: 'SiLabVet | Informe de resultado — Ectoparásitos',
  },
  // Endoparásitos: una sola bandeja y una sola hoja para todas las especies;
  // el panel de parásitos lo decide el backend con la especie de la recepción.
  {
    path: 'parasitologia/endoparasitos',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Endoparásitos',
    data: {
      pantalla: 'ENDOPARASITOS',
      breadcrumb: SECCION,
      titulo: 'Endoparásitos',
      permiso: Permiso.ENDOPARASITOS_VER,
    },
  },
  {
    path: 'parasitologia/endoparasitos/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'ENDOPARASITOS',
      permiso: [Permiso.ENDOPARASITOS_RESULTADOS, Permiso.ENDOPARASITOS_VER],
    },
    loadComponent: () =>
      import('./pages/endoparasitos-resultados/endoparasitos-resultados.page').then(
        (m) => m.EndoparasitosResultadosPage,
      ),
    title: 'SiLabVet | Informe de resultado — Endoparásitos',
  },
];
